/**
 * The file policy, mirrored from the API (apps/api/src/modules/documents/domain/upload-policy.ts). Every upload dialog
 * checks a file with `checkUpload(file, purpose)` before asking for an upload intent, so the browser, the API, the
 * parsers and the proxy agree. `upload-policy.test.ts` imports the API file and fails when the two drift.
 *
 *   document / settlement / shortfall_evidence   PDF, JPEG, PNG ≤ 15 MB
 *   attachment                                   PDF, JPEG, PNG ≤ 15 MB, or XLSX / CSV ≤ 10 MB
 *   recon_statement                              CSV or XLSX ≤ 10 MB, ≤ 20,000 rows
 *   library                                      PDF, JPG, PNG, DOC, DOCX, XLS, XLSX, XLSM, CSV, PPT, PPTX, TXT ≤ 25 MB
 *   import (multipart POST /imports)             XLSX, XLSM or CSV ≤ 10 MB, ≤ 20,000 rows
 */
import type { Schema } from '@dealdesk/api-schema';

export const MB = 1024 * 1024;

export const PDF = 'application/pdf';
export const JPEG = 'image/jpeg';
export const PNG = 'image/png';
export const XLSX = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
export const XLSM = 'application/vnd.ms-excel.sheet.macroEnabled.12';
export const CSV = 'text/csv';
export const TXT = 'text/plain';
export const DOC = 'application/msword';
export const DOCX = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
export const XLS = 'application/vnd.ms-excel';
export const PPT = 'application/vnd.ms-powerpoint';
export const PPTX = 'application/vnd.openxmlformats-officedocument.presentationml.presentation';

export type UploadPurpose = Schema<'UploadPurpose'>;
/** Purposes an upload intent may ask for (the contract's UploadPurpose enum). */
export const UPLOAD_PURPOSES = ['document', 'settlement', 'shortfall_evidence', 'attachment', 'recon_statement', 'library'] as const satisfies readonly UploadPurpose[];
/** Intent purposes plus multipart tracker imports. */
export type FilePurpose = UploadPurpose | 'import';

export interface FileRule {
  contentTypes: readonly string[];
  extensions: readonly string[];
  maxBytes: number;
}

export interface PurposePolicy {
  rules: readonly FileRule[];
  maxRows?: number;
}

const DOCUMENT_RULE: FileRule = { contentTypes: [PDF, JPEG, PNG], extensions: ['.pdf', '.jpg', '.jpeg', '.png'], maxBytes: 15 * MB };
const SPREADSHEET_RULE: FileRule = { contentTypes: [XLSX, CSV], extensions: ['.xlsx', '.csv'], maxBytes: 10 * MB };

export const MAX_PARSED_ROWS = 20_000;

const LIBRARY_RULE: FileRule = {
  contentTypes: [PDF, JPEG, PNG, DOC, DOCX, XLS, XLSX, XLSM, CSV, PPT, PPTX, TXT],
  extensions: ['.pdf', '.jpg', '.jpeg', '.png', '.doc', '.docx', '.xls', '.xlsx', '.xlsm', '.csv', '.ppt', '.pptx', '.txt'],
  maxBytes: 25 * MB,
};

export const FILE_POLICY: Record<FilePurpose, PurposePolicy> = {
  document: { rules: [DOCUMENT_RULE] },
  settlement: { rules: [DOCUMENT_RULE] },
  shortfall_evidence: { rules: [DOCUMENT_RULE] },
  attachment: { rules: [DOCUMENT_RULE, SPREADSHEET_RULE] },
  recon_statement: { rules: [SPREADSHEET_RULE], maxRows: MAX_PARSED_ROWS },
  library: { rules: [LIBRARY_RULE] },
  import: { rules: [{ contentTypes: [XLSX, XLSM, CSV], extensions: ['.xlsx', '.xlsm', '.csv'], maxBytes: 10 * MB }], maxRows: MAX_PARSED_ROWS },
};

const extensionOf = (name: string) => {
  const dot = name.lastIndexOf('.');
  return dot >= 0 ? name.slice(dot).toLowerCase() : '';
};

/**
 * The content type the API should be told: the one the file extension maps to when the purpose allows that
 * extension (the API requires extension and type to agree; browsers report CSV as text/csv, application/vnd.ms-excel
 * or nothing at all), else the browser's own.
 */
export function contentTypeOf(file: Pick<File, 'name' | 'type'>, purpose: FilePurpose = 'document'): string {
  const declared = file.type.split(';')[0]?.trim().toLowerCase() ?? '';
  const rules = FILE_POLICY[purpose].rules;
  const ext = extensionOf(file.name);
  const byExtension = rules.some((r) => r.extensions.includes(ext)) ? TYPE_OF_EXTENSION[ext] : undefined;
  return byExtension ?? declared;
}

const TYPE_OF_EXTENSION: Record<string, string> = {
  '.pdf': PDF,
  '.jpg': JPEG,
  '.jpeg': JPEG,
  '.png': PNG,
  '.xlsx': XLSX,
  '.xlsm': XLSM,
  '.csv': CSV,
  '.doc': DOC,
  '.docx': DOCX,
  '.xls': XLS,
  '.ppt': PPT,
  '.pptx': PPTX,
  '.txt': TXT,
};

export type UploadProblem = { code: 'type'; types: string } | { code: 'too_large'; limitMb: number } | { code: 'empty' };

/** Labels of the allowed types of a purpose (PDF, JPG, PNG, XLSX, CSV …) for messages and hints. */
export function typeLabels(purpose: FilePurpose): string {
  const labels = FILE_POLICY[purpose].rules.flatMap((r) => r.extensions.filter((e) => e !== '.jpeg').map((e) => e.slice(1).toUpperCase()));
  return [...new Set(labels)].join(', ');
}

/** The largest file of a purpose in MB (for hints). */
export const maxMb = (purpose: FilePurpose) => Math.max(...FILE_POLICY[purpose].rules.map((r) => r.maxBytes)) / MB;

/** Checks a file against the purpose's policy: allowed type (by type or extension) and that type's size limit. */
export function checkUpload(file: Pick<File, 'name' | 'type' | 'size'>, purpose: FilePurpose = 'document'): UploadProblem | null {
  if (file.size <= 0) return { code: 'empty' };
  const type = contentTypeOf(file, purpose);
  const rule = FILE_POLICY[purpose].rules.find((r) => r.contentTypes.some((c) => c.toLowerCase() === type.toLowerCase()));
  if (!rule) return { code: 'type', types: typeLabels(purpose) };
  if (file.size > rule.maxBytes) return { code: 'too_large', limitMb: rule.maxBytes / MB };
  return null;
}

/** `accept` attribute for a file input of the purpose (types and extensions). */
export function acceptFor(purpose: FilePurpose): string {
  return [...new Set(FILE_POLICY[purpose].rules.flatMap((r) => [...r.contentTypes, ...r.extensions]))].join(',');
}
